import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { errText } from "@/lib/api";
import { Panel, Btn, inputCls, useOps } from "./ui";
import { Thread, StageControl } from "./PlayerDetail";

export default function TestInbox() {
  const { api } = useOps();
  const [players, setPlayers] = useState([]);
  const [email, setEmail] = useState("tester1@test.333sos");
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ subject: "", text: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const loadPlayers = useCallback(() => api.get("/test/players").then((r) => setPlayers(r.data)), [api]);
  const current = players.find((p) => p.email === email.trim().toLowerCase());
  const loadThread = useCallback(() => {
    if (!current) { setDetail(null); return; }
    api.get(`/players/${current.id}`).then((r) => setDetail(r.data)).catch(() => setDetail(null));
  }, [api, current]);

  useEffect(() => { loadPlayers(); }, [loadPlayers]);
  useEffect(() => { loadThread(); const id = setInterval(loadThread, 3000); return () => clearInterval(id); }, [loadThread]);

  const send = async (e) => {
    e.preventDefault(); setBusy(true);
    const fd = new FormData();
    fd.append("email", email); fd.append("subject", form.subject); fd.append("text", form.text);
    if (file) fd.append("file", file);
    try {
      const r = await api.post("/test/send", fd);
      toast.success(`Routed: ${r.data.outcome}`);
      setForm({ subject: "", text: "" }); setFile(null); if (fileRef.current) fileRef.current.value = "";
      await loadPlayers();
    } catch (x) { toast.error(errText(x)); } finally { setBusy(false); }
  };
  const bulk = async (call, msg) => {
    setBusy(true);
    try { const r = await call(); toast.success(msg(r)); await loadPlayers(); }
    catch (x) { toast.error(errText(x)); } finally { setBusy(false); }
  };
  const skip = async () => {
    if (!current) return;
    setBusy(true);
    try { await api.post(`/test/players/${current.id}/skip-delays`); loadThread(); toast.success("Delays skipped"); }
    catch (x) { toast.error(errText(x)); } finally { setBusy(false); }
  };

  return (
    <div className="grid gap-3 lg:grid-cols-[300px_1fr]" data-testid="admin-test-inbox">
      <Panel title="Test players">
        <div className="mb-2 flex flex-wrap gap-1.5">
          <Btn variant="ghost" disabled={busy} onClick={() => bulk(() => api.post("/test/seed"), (r) => `${r.data.created.length} seeded test players`)} data-testid="test-seed-players">Seed</Btn>
          <Btn variant="danger" disabled={busy} onClick={() => window.confirm("Delete ALL test players and their media?") && bulk(() => api.post("/test/clear"), (r) => `${r.data.deleted} test players deleted`)} data-testid="test-clear-players">Clear all</Btn>
          <Btn variant="ghost" disabled={busy} onClick={() => bulk(() => api.post("/test/run-nudges"), (r) => `${r.data.queued} nudges queued`)} data-testid="test-run-nudges">Run nudge check</Btn>
        </div>
        <p className="mb-2 text-[11px] text-slate-500">Nothing here is really emailed. Pick or type any fake address.</p>
        {players.map((p) => (
          <button key={p.id} type="button" onClick={() => setEmail(p.email)} data-testid={`test-player-${p.email}`}
            className={`block w-full border-b border-[#243144] px-1 py-1.5 text-left text-xs hover:bg-[#1a2332] ${current?.id === p.id ? "bg-[#1a2332]" : ""}`}>
            <div className="text-slate-200">{p.email}</div><div className="mono text-[10px] text-slate-500">{p.serial} · {p.stage}</div>
          </button>
        ))}
      </Panel>
      <div className="space-y-3">
        <Panel title="Send to M as…" testid="admin-test-compose">
          <form onSubmit={send} className="space-y-2">
            <input data-testid="test-from-email-input" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="fake sender address" />
            <input data-testid="test-subject-input" className={inputCls} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="subject (optional, e.g. signal ABCD)" />
            <textarea data-testid="test-body-input" className={`${inputCls} h-20`} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="message (try: help, stop, delete)" />
            <div className="flex flex-wrap items-center gap-2">
              <input ref={fileRef} data-testid="test-photo-input" type="file" accept="image/*,.heic,.heif" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-xs text-slate-400" />
              <Btn type="submit" disabled={busy || !email} data-testid="test-send-button" className="ml-auto">Send to M</Btn>
              <Btn variant="ghost" onClick={skip} disabled={busy || !current} data-testid="test-skip-delays-button">Skip delays</Btn>
            </div>
          </form>
        </Panel>
        {detail && (
          <Panel title={`${detail.player.serial} · ${detail.player.stage}`} testid="admin-test-inbox-chat" actions={<StageControl player={detail.player} stages={detail.stages} onDone={loadThread} />}>
            <Thread messages={detail.messages} />
          </Panel>
        )}
      </div>
    </div>
  );
}
